/**
 * @fileoverview Rich-text editor for instructions displayed in a template header.
 * @module ui-admin/components/template-builder/HeaderRichEditor
 *
 * Responsibilities:
 * - Edit header instructions with formatting, color, alignment, and image controls.
 * - Synchronize edited HTML with the template builder through a callback.
 * Dependencies: React, Tiptap extensions, lucide-react.
 * Architecture: Controlled editor surface; HTML content and updates are supplied by the parent.
 * Notes: Text edits are emitted after a short debounce; pending timers are cleared on unmount.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { FontFamily } from '@tiptap/extension-font-family';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Image as ImageIcon, Minus,
  Heading1, Heading2, Heading3, Type, Palette, Highlighter,
  RotateCcw,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HeaderRichEditorProps {
  content: string;
  onChange: (html: string) => void;
}

// ---------------------------------------------------------------------------
// Toolbar helpers
// ---------------------------------------------------------------------------

const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const TEXT_COLORS = [
  '#000000', '#374151', '#6B7280', '#EF4444', '#F97316',
  '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899',
  '#FFFFFF', '#FEF3C7', '#FEE2E2', '#DBEAFE', '#F3E8FF',
];

const HIGHLIGHT_COLORS = [
  '#FEF08A', '#BBF7D0', '#BFDBFE', '#FDE68A', '#FBCFE8',
  '#E0E7FF', '#FFEDD5', '#FCE7F3', '#D1FAE5', '#E0F2FE',
  'transparent',
];

function ToolbarButton({
  onClick,
  active,
  title,
  children,
  disabled,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-amber-100 text-amber-700'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-gray-200 mx-0.5 self-center" />;
}

function ColorPicker({
  colors,
  onSelect,
  title,
  icon,
  activeColor,
}: {
  colors: string[];
  onSelect: (color: string) => void;
  title: string;
  icon: React.ReactNode;
  activeColor?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen((p) => !p); }}
        title={title}
        className="flex items-center gap-0.5 p-1.5 rounded-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
      >
        {icon}
        <div
          className="w-3 h-1 rounded-xs border border-gray-300 mt-0.5"
          style={{ backgroundColor: activeColor || 'transparent' }}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex flex-wrap gap-1 w-[140px]">
          {colors.map((color) => (
            <button
              key={color}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(color); setOpen(false); }}
              title={color}
              className="w-5 h-5 rounded-sm border border-gray-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: color === 'transparent' ? undefined : color }}
            >
              {color === 'transparent' && (
                <span className="text-[8px] text-gray-400 leading-none">✕</span>
              )}
            </button>
          ))}
          {/* Custom color picker */}
          <label className="w-5 h-5 rounded-sm border border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors" title="Custom color">
            <span className="text-[8px] text-gray-400">+</span>
            <input
              type="color"
              className="sr-only"
              onChange={(e) => { onSelect(e.target.value); setOpen(false); }}
            />
          </label>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image URL dialog
// ---------------------------------------------------------------------------

function ImageDialog({
  onInsert,
  onClose,
}: {
  onInsert: (url: string, alt: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-5 w-[380px]">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Insert Image</h3>
        <label className="block text-xs text-gray-600 mb-1">Image URL</label>
        <input
          autoFocus
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/image.png"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3 focus:outline-hidden focus:ring-2 focus:ring-amber-300"
        />
        <label className="block text-xs text-gray-600 mb-1">Alt text (optional)</label>
        <input
          type="text"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder="Describe the image…"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-4 focus:outline-hidden focus:ring-2 focus:ring-amber-300"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { if (url) { onInsert(url, alt); onClose(); } }}
            disabled={!url}
            className="px-3 py-1.5 text-sm text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-40"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeaderRichEditor
// ---------------------------------------------------------------------------

/**
 * @component HeaderRichEditor
 * Purpose: Author rich-text instructions for an inspection template header.
 * Props: Current HTML content and a callback receiving updated HTML.
 * Behavior: Provides formatting tools, image insertion, clearing, and external-content synchronization.
 * Data Source: local (controlled HTML prop and in-memory Tiptap editor state).
 * Constraints: The parent owns persistence and any image hosting workflow.
 * Performance: Debounces text changes by 400 ms and cancels pending updates on unmount.
 */
export function HeaderRichEditor({ content, onChange }: HeaderRichEditorProps) {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = useRef<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, underline: false }),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: false, allowBase64: true }),
    ],
    content: content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-hidden min-h-[120px] px-3 py-3 text-gray-800',
      },
    },
    onUpdate({ editor: ed }: { editor: any }) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const html = ed.getHTML();
        lastEmittedRef.current = html;
        onChange(html);
      }, 400);
    },
  });

  // Sync external content changes (e.g. undo from parent).
  // Skip if the incoming value is what we last emitted ourselves to avoid a
  // setContent → onUpdate → onChange → setContent loop caused by Tiptap
  // serialising semantically-equivalent HTML differently each round-trip.
  useEffect(() => {
    if (!editor) return;
    if (content === lastEmittedRef.current) return;
    if (content !== editor.getHTML()) {
      editor.commands.setContent(content || '<p></p>');
    }
  }, [content, editor]);

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  const insertImage = useCallback((url: string, alt: string) => {
    editor?.chain().focus().setImage({ src: url, alt }).run();
  }, [editor]);

  const activeTextColor = editor?.getAttributes('textStyle')?.color as string | undefined;
  const activeHighlight = editor?.getAttributes('highlight')?.color as string | undefined;

  const activeFontFamily = editor?.getAttributes('textStyle')?.fontFamily as string | undefined;

  if (!editor) return null;

  return (
    <div
      className="border-t border-amber-100 bg-white"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Label */}
      <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
        <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider select-none">
          Header Instructions
        </p>
        <button
          type="button"
          title="Clear all content"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().clearContent().run(); onChange(''); }}
          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-500 transition-colors"
        >
          <RotateCcw size={10} /> Clear
        </button>
      </div>

      {/* Toolbar */}
      <div className="px-2 py-1.5 border-y border-amber-100 bg-amber-50/40 flex flex-wrap items-center gap-0.5">

        {/* Heading / paragraph */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')}
          title="Paragraph"
        >
          <Type size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 size={13} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Font family */}
        <select
          title="Font family"
          value={activeFontFamily || ''}
          onChange={(e) => {
            const val = e.target.value;
            if (val) {
              editor.chain().focus().setFontFamily(val).run();
            } else {
              editor.chain().focus().unsetFontFamily().run();
            }
          }}
          className="text-[11px] h-6 border border-gray-200 rounded-sm px-1 text-gray-600 bg-white focus:outline-hidden focus:ring-1 focus:ring-amber-300"
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <ToolbarDivider />

        {/* Inline styles */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <Bold size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <Italic size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
        >
          <UnderlineIcon size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough size={13} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Text color */}
        <ColorPicker
          colors={TEXT_COLORS}
          onSelect={(color) => editor.chain().focus().setColor(color).run()}
          title="Text color"
          icon={<Palette size={13} />}
          activeColor={activeTextColor}
        />

        {/* Highlight / background color */}
        <ColorPicker
          colors={HIGHLIGHT_COLORS}
          onSelect={(color) => {
            if (color === 'transparent') {
              editor.chain().focus().unsetHighlight().run();
            } else {
              editor.chain().focus().setHighlight({ color }).run();
            }
          }}
          title="Highlight color"
          icon={<Highlighter size={13} />}
          activeColor={activeHighlight}
        />

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align left"
        >
          <AlignLeft size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align center"
        >
          <AlignCenter size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align right"
        >
          <AlignRight size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
          title="Justify"
        >
          <AlignJustify size={13} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <List size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <ListOrdered size={13} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Horizontal rule */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal rule"
        >
          <Minus size={13} />
        </ToolbarButton>

        {/* Image */}
        <ToolbarButton
          onClick={() => setShowImageDialog(true)}
          title="Insert image"
        >
          <ImageIcon size={13} />
        </ToolbarButton>
      </div>

      {/* Editor content area */}
      <div className="min-h-[120px] max-h-[480px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      {/* Image dialog — portalled to body to avoid clipping by ancestor CSS transforms */}
      {showImageDialog && createPortal(
        <ImageDialog
          onInsert={insertImage}
          onClose={() => setShowImageDialog(false)}
        />,
        document.body
      )}
    </div>
  );
}

export default HeaderRichEditor;
